import { useState, useEffect, useCallback } from "react";

const DEFAULT_FLASH_DURATION_MS = 1100;

/**
 * Hook for managing flash animation state on items.
 *
 * Two modes:
 * - One-shot: `triggerFlash(id)` flashes an item for a fixed duration then auto-clears.
 *   Used for focus indicators (e.g., VeroGo toggle feedback).
 * - Async: `startFlash(id)` starts a persistent flash, `stopFlash()` clears it.
 *   Used for loading states (e.g., custom doc creation/deletion).
 *
 * `isFlashing(id)` returns true if the given item is currently flashing.
 */
export const useFlashState = () => {
  const [flashId, setFlashId] = useState<string | number | null>(null);
  const [autoClearing, setAutoClearing] = useState(false);

  useEffect(() => {
    if (flashId === null || !autoClearing) return;
    const timer = setTimeout(() => {
      setFlashId(null);
      setAutoClearing(false);
    }, DEFAULT_FLASH_DURATION_MS);
    return () => clearTimeout(timer);
  }, [flashId, autoClearing]);

  // One-shot flash with auto-clear
  const triggerFlash = useCallback((id: string | number) => {
    setFlashId(id);
    setAutoClearing(true);
  }, []);

  // Persistent flash — stays until stopFlash() is called
  const startFlash = useCallback((id: string | number) => {
    setFlashId(id);
    setAutoClearing(false);
  }, []);

  const stopFlash = useCallback(() => {
    setFlashId(null);
    setAutoClearing(false);
  }, []);

  const isFlashing = useCallback(
    (id: string | number) => flashId === id,
    [flashId]
  );

  return { triggerFlash, startFlash, stopFlash, isFlashing, flashId };
};
